/*
  nsi-variant-price-sync.js — the authoritative "which variant is selected, and what does
  it cost" signal for the product page, and the repair crew for the theme's own price DOM.

  WHY THIS FILE EXISTS (Zac, 2026-09-10). Three scripts write to the same price region and
  they fight:

    1. superstore.min.js (the theme) caches its price nodes ONCE, at construction:
         this.variantFields = { $priceMoney: this.$details.find("[data-price] .money"), ... }
       and on every variant change it calls $priceMoney.text(formatMoney(variant.price)).
       It never re-queries. It also runs
         history.replaceState({}, "variant", handle + "?variant=" + id)
       on every selection (sections/static-product.liquid sets "enableHistory": true), so a
       shopper who arrives on a bare URL is put on a ?variant= URL after their first click.

    2. BSS B2B & Wholesale stamps [bss-b2b-product-price] on .product--price and then does
         e.innerHTML = (comparePriceHTML || originalPriceHTML || "") + priceHTML + (unitPriceHTML || "")
       (bss-b2b-chunk-cp.js). That REPLACES the wrapper's children with bare
       <span style="...">$X.XX</span> nodes — no .money, no [data-price]. The variant id it
       binds to is read once, at first paint:
         B = ShopifyAnalytics.meta.selectedVariantId || meta.product.variants[0].id
       and stamped as bss-b2b-variant-id. It is never re-read.

    3. nsi-trade-price.js mirrors [data-price] into the three-tier trade line via a
       MutationObserver on [data-price].

    The moment BSS rewrites the wrapper, the theme's cached .money node and the trade
    line's observed [data-price] node are both DETACHED. The theme keeps computing the
    right price and writing it into a node that is no longer on the page; the trade
    observer never fires again. The visible price freezes at whatever was rendered for
    the variant resolved at page load — i.e. the one in ?variant= — and sticks until a
    reload. Verified in headless Chrome: after the wrapper wipe, picking $11.95 / $6.50 /
    $4.00 variants left "$3.50" on screen every time while the theme wrote 11.95, 6.50 and
    4.00 into the detached node.

  WHAT THIS FILE DOES: on every variant change it re-resolves the selected variant from
  the page (never a cached id), reads the price and compare-at from the product's own
  JSON (never scraped text), repairs the theme's price markup if an app destroyed it, and
  fires `nsi:variant-price` on document so the trade line can redraw from real numbers.
  The client authorised re-resolving on every click, so nothing here tries to be clever
  about skipping work.
*/
(function () {
  'use strict';

  var EVENT = 'nsi:variant-price';
  var jsonCache = {};        // handle -> Promise<variant map>
  var scopes = [];           // tracked product scopes

  /* ---------- money ---------- */
  function fmt(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function themeMoney(cents) {
    /* Use the theme's own formatter when it is loaded so a currency/format change in the
       customizer follows automatically; fall back to the plain AUD shape otherwise. */
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.Shopify.money_format) {
        return Shopify.formatMoney(cents, Shopify.money_format);
      }
    } catch (e) {}
    return fmt(cents);
  }

  /* ---------- scope + handle ---------- */
  function findScopes() {
    var found = [];
    var pdp = document.getElementById('shopify-section-static-product');
    if (pdp) found.push(pdp);
    var others = document.querySelectorAll('[data-product-details]');
    for (var i = 0; i < others.length; i++) {
      var s = others[i].closest('[data-product-wrapper]') || others[i].parentElement || others[i];
      if (found.indexOf(s) === -1 && !(pdp && pdp.contains(s))) found.push(s);
    }
    return found;
  }

  function handleFor(scope) {
    /* the PDP's own URL is the truthful handle; a quickshop carries the product URL on the
       element the theme opened it from, and ShopifyAnalytics is the last resort */
    var m;
    var link = scope.querySelector('[data-product-quickshop-url]');
    if (link) {
      m = String(link.getAttribute('data-product-quickshop-url')).match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }
    var full = scope.querySelector('[data-product-full-details], a.product-full-details, [data-product-url]');
    if (full) {
      m = String(full.getAttribute('href') || full.getAttribute('data-product-url') || '').match(/\/products\/([^/?#]+)/);
      if (m) return m[1];
    }
    m = String(location.pathname).match(/\/products\/([^/?#]+)/);
    if (m) return m[1];
    try {
      if (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product) {
        return ShopifyAnalytics.meta.product.handle || null;
      }
    } catch (e) {}
    return null;
  }

  /* ---------- variant data ---------- */
  function seedFromAnalytics() {
    /* synchronous, so the very first redraw is right even before the fetch lands.
       ShopifyAnalytics carries price but never compare_at_price — the fetch fills that in. */
    var map = {};
    try {
      var vs = window.ShopifyAnalytics.meta.product.variants;
      for (var i = 0; i < vs.length; i++) {
        map[String(vs[i].id)] = { id: String(vs[i].id), price: vs[i].price, compareAt: null, available: null, partial: true };
      }
    } catch (e) {}
    return map;
  }

  /* long enough that a burst of clicks cannot re-fire a failing request, short enough that a
     genuine blip recovers on the shopper's next interaction */
  var FETCH_COOLDOWN_MS = 30000;

  function loadVariants(handle) {
    if (!handle) return Promise.resolve(seedFromAnalytics());
    if (jsonCache[handle]) return jsonCache[handle];
    jsonCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (p) {
        var map = {};
        for (var i = 0; i < p.variants.length; i++) {
          var v = p.variants[i];
          map[String(v.id)] = {
            id: String(v.id),
            price: v.price,
            compareAt: (v.compare_at_price == null ? null : v.compare_at_price),
            available: !!v.available,
            partial: false
          };
        }
        return map;
      })
      .catch(function () {
        /* Never leave the page without numbers: fall back to what is already in the DOM.
           The rejection is CACHED for a cooldown rather than deleted. Deleting it meant a
           failing fetch was never negative-cached, and since every interaction schedules four
           forced resolves (0/150/500/1400 ms) that turned one flaky request into a permanent
           retry loop against Shopify, four deep per click, with the price blank throughout. */
        setTimeout(function () { delete jsonCache[handle]; }, FETCH_COOLDOWN_MS);
        return seedFromAnalytics();
      });
    return jsonCache[handle];
  }

  /* ---------- which variant is selected, RIGHT NOW ---------- */
  function urlVariant() {
    var m = String(location.search).match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  function selectedId(scope) {
    /* Re-read every time. Nothing here is cached, because a cached id is the bug. */
    var el = scope.querySelector('select[name="id"], input[name="id"]');
    if (el && el.value && /^\d+$/.test(String(el.value).trim())) return String(el.value).trim();

    /* Fall back to matching the visible option selects against the no-js option list.
       Matched by PREFIX, because the theme suffixes each option's label with its price
       (" - $13.60") or its sold-out wording (" - Sold out"), and on a sold-out variant
       this theme puts that whole label in the option's value instead of the variant id —
       which is also why select[name="id"] can come back empty on such a product
       (moonlight-cat-eye-magnetic-gel-born-pretty is one). */
    var opts = scope.querySelectorAll('[data-product-option]');
    if (opts.length) {
      var parts = [];
      for (var i = 0; i < opts.length; i++) parts.push(String(opts[i].value).trim());
      var want = parts.join(' / ');
      var list = scope.querySelectorAll('select[name="id"] option, [data-variants] option');
      for (var j = 0; j < list.length; j++) {
        var txt = String(list[j].textContent).replace(/\s+/g, ' ').trim();
        if (txt === want || txt.indexOf(want + ' - ') === 0) {
          var vid = String(list[j].getAttribute('data-variant-id') || list[j].value);
          if (/^\d+$/.test(vid)) return vid;
        }
      }
    }
    var u = urlVariant();
    if (u) return u;
    var first = scope.querySelector('select[name="id"] option, [data-variants] option');
    return first ? String(first.value) : null;
  }

  /* ---------- repair the theme's own price markup ---------- */
  function wrapperHidden(wrap) {
    if (!wrap) return true;
    if (wrap.classList.contains('nsi-price--superseded')) return true;   // trade customer: hidden by nsi-site.css
    var cs;
    try { cs = window.getComputedStyle(wrap); } catch (e) { return false; }
    if (!cs) return false;
    return cs.display === 'none' || cs.visibility === 'hidden' || !wrap.getClientRects().length;
  }

  function nudgeApps(scope, budget) {
    if (budget.nudged) return;
    budget.nudged = true;
    var input = scope.querySelector('select[name="id"], input[name="id"]');
    if (!input) return;
    try { input.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
  }

  function applyToThemePrice(scope, v, budget) {
    var wrap = scope.querySelector('.product--price, [bss-b2b-product-price]');
    if (!wrap) return;
    var priceEl = wrap.querySelector('[data-price]');
    var cmpEl = wrap.querySelector('[data-price-compare-at]');
    var onSale = v.compareAt != null && v.compareAt > v.price;

    if (!priceEl) {
      /* An app replaced the wrapper's children (BSS does exactly this). Rebuild the
         theme's own nodes so every downstream reader — the trade line, the sticky
         add-to-cart bar, anything querying [data-price] — has a live source again.
         Only ever rebuilt while the wrapper is NOT the price the shopper is looking at
         (trade customers have it display:none, via .nsi-price--superseded).

         When an app owns a VISIBLE price line, that number is the one the customer will
         be charged, so we never paint a retail figure over it. Instead we nudge the app
         to re-resolve: BSS reads the selected variant off the form's variant input
         (detectVariantChangeByFormId) and has a trigger bound to it
         (bss-b2b-product-trigger-added="true"), but the theme sets that select's value
         silently, so BSS is never told the variant moved and keeps showing the price it
         rendered at page load. One synthetic change event, once per variant change, is
         what re-resolves it — with BSS's own numbers, not ours. */
      if (!wrapperHidden(wrap)) { nudgeApps(scope, budget); return; }
      if (budget.left <= 0) return;
      budget.left--;
      cmpEl = document.createElement('div');
      cmpEl.className = 'price--compare-at';
      cmpEl.setAttribute('data-price-compare-at', '');
      cmpEl.setAttribute('data-nsi-restored', '');
      cmpEl.innerHTML = '<span class="money"></span>';
      priceEl = document.createElement('div');
      priceEl.className = 'price--main';
      priceEl.setAttribute('data-price', '');
      priceEl.setAttribute('data-nsi-restored', '');
      priceEl.innerHTML = '<span class="money"></span>';
      wrap.appendChild(cmpEl);
      wrap.appendChild(priceEl);
    }

    var money = priceEl.querySelector('.money');
    if (!money) { money = document.createElement('span'); money.className = 'money'; priceEl.appendChild(money); }
    var want = themeMoney(v.price);
    if (String(money.textContent).trim() !== want) money.textContent = want;

    /* Same rule as the trade line: a partial seed has not learned the compare-at yet, so its
       null means "unknown", not "no markdown". Writing '' here cleared the struck price the
       server had already rendered, on every load of every discounted product. */
    if (cmpEl && !v.partial) {
      var cmpMoney = cmpEl.querySelector('.money');
      if (!cmpMoney) { cmpMoney = document.createElement('span'); cmpMoney.className = 'money'; cmpEl.appendChild(cmpMoney); }
      var wantCmp = onSale ? themeMoney(v.compareAt) : '';
      if (String(cmpMoney.textContent).trim() !== wantCmp) cmpMoney.textContent = wantCmp;
      cmpEl.classList.toggle('visible', onSale);
    }
  }

  /* ---------- the loop ---------- */
  function emit(scope, v) {
    document.dispatchEvent(new CustomEvent(EVENT, {
      detail: { scope: scope, id: v.id, price: v.price, compareAt: v.compareAt, available: v.available, partial: !!v.partial }
    }));
  }

  function resolve(scope, force) {
    var state = scope.__nsiPriceState;
    var id = selectedId(scope);
    if (!id) return;
    if (!force && state.lastId === id && state.settled) return;
    var changed = state.lastId !== id;
    state.lastId = id;
    if (changed) { state.budget.left = 4; state.budget.nudged = false; }   // per change, so a wrapper-wiping app cannot start a ping-pong

    var paint = function (map) {
      /* the JSON fetch can land after the shopper has moved on — never paint a variant
         that is no longer the selected one */
      if (state.lastId !== id) return;
      var v = map[id];
      if (!v) { state.settled = false; return; }
      state.settled = !v.partial;
      state.writing++;
      try { applyToThemePrice(scope, v, state.budget); } finally {
        setTimeout(function () { if (state.writing > 0) state.writing--; }, 0);
      }
      if (state.observe) state.observe();   // the wrapper may have been rebuilt under us
      emit(scope, v);
    };

    var seed = seedFromAnalytics();
    if (seed[id]) paint(seed);                      // instant, price-only
    loadVariants(state.handle).then(paint);          // authoritative, with compare-at
  }

  function watch(scope) {
    if (scope.__nsiPriceState) return;
    var st = { handle: handleFor(scope), lastId: null, settled: false, writing: 0, timers: [], queued: false,
               budget: { left: 4, nudged: false } };
    scope.__nsiPriceState = st;

    /* Coalesced. Every trigger asks for ONE re-resolve plus one set of follow-ups; a new
       trigger replaces the outstanding follow-ups instead of stacking a fresh set on top.
       Without this, our own DOM writes feed the MutationObserver, which schedules more
       work, which writes again — a timer storm that locks the main thread. */
    function run(force) { st.queued = false; try { resolve(scope, force); } catch (e) {} }
    function kick(force) {
      for (var i = 0; i < st.timers.length; i++) clearTimeout(st.timers[i]);
      st.timers.length = 0;
      if (!st.queued) { st.queued = true; setTimeout(function () { run(!!force); }, 0); }
      /* apps re-render the option selects and the price region a beat late (BSS, swatch
         apps, currency apps). Re-resolving a few times costs nothing and is what the
         client asked for: correctness over cleverness. */
      st.timers.push(setTimeout(function () { run(true); }, 150));
      st.timers.push(setTimeout(function () { run(true); }, 500));
      st.timers.push(setTimeout(function () { run(true); }, 1400));
    }
    st.kick = kick;

    /* capture phase, so a handler that stops propagation cannot hide the change from us */
    document.addEventListener('change', function (e) { if (scope.contains(e.target)) kick(); }, true);
    document.addEventListener('click', function (e) { if (scope.contains(e.target)) kick(); }, true);
    window.addEventListener('popstate', function () { kick(true); });

    /* the theme rewrites the URL with history.replaceState, which fires no event */
    var lastHref = location.href;
    setInterval(function () {
      if (location.href !== lastHref) { lastHref = location.href; kick(true); }
    }, 250);

    /* an app can swap the price wrapper or the selects without firing any event at all.
       Watched narrowly, and never for mutations we made ourselves. */
    var mo = new MutationObserver(function (recs) {
      if (st.writing) return;
      for (var i = 0; i < recs.length; i++) {
        var t = recs[i].target;
        if (t && t.nodeType === 1 && t.closest && t.closest('[data-nsi-restored]')) continue;
        kick();
        return;
      }
    });
    st.observe = function () {
      mo.disconnect();
      var wrap = scope.querySelector('.product--price, [bss-b2b-product-price]');
      if (wrap) mo.observe(wrap, { childList: true, subtree: true, characterData: true });
      var sel = scope.querySelectorAll('select[name="id"], input[name="id"], [data-product-option]');
      for (var i = 0; i < sel.length; i++) mo.observe(sel[i], { attributes: true, attributeFilter: ['value', 'selected'] });
    };
    st.observe();
    kick(true);
  }

  function scan() {
    var found = findScopes();
    for (var i = 0; i < found.length; i++) {
      if (scopes.indexOf(found[i]) === -1) { scopes.push(found[i]); watch(found[i]); }
    }
  }

  function init() {
    scan();
    /* quickshop modals and section re-renders add whole product scopes after load */
    new MutationObserver(function () { scan(); }).observe(document.body, { childList: true, subtree: true });
  }

  window.NSIVariantPriceSync = {
    event: EVENT,
    refresh: function () { for (var i = 0; i < scopes.length; i++) resolve(scopes[i], true); },
    selectedId: selectedId,
    format: fmt
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
